import debounce from "debounce-promise";
import CybwellError from "./CybwellError";
import { get } from "../http";
import {
  mapAddressDetailResponse,
  mapCompanyDetailResponse,
  mapGetBankByIBANResponse,
  mapSuggestAddressResponse,
  mapSuggestCompanyResponse,
} from "./mapper";
import NetworkError from "../http/networkError";
import {
  AdressDetailResponse,
  AdressesResponse,
  BankResponse,
  CompaniesResponse,
  CompanyDetailResponse,
  CybwellResponse,
} from "./types";

const CYBWELL_SERVICE_URL =
  "https://webservice-swisscard.cybwell.ch/onboarding";

const timeout = 15 * 1000;

interface Credentials {
  token: string;
  clientIpOverride?: string;
}

const debounceCallbackTimeout = 300;

let credentialsData: Credentials;
let credentialsDataLastUpdated: Date;
const credentialsExpireTimeInHours = 1;

const resolveCredentialsUrlParams = (credentials: Credentials) =>
  credentials.clientIpOverride
    ? { token: credentials.token, clientIp: credentials.clientIpOverride }
    : { token: credentials.token };

const processResponse = (response: Response) => {
  const contentType = response.headers.get("content-type");

  if (response.ok) {
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    }

    return response.text();
  }

  throw new NetworkError(
    "Cybwell request resulted in an error",
    response.status,
  );
};

/**
 * The authentication call (if no token present). If there let's reuse it.
 */
const authenticate = (): Promise<Credentials> =>
  get(
    "/cybwell/api/token",
    undefined,
    {
      credentials: "include",
    },
    timeout,
  )
    .then((response) => processResponse(response))
    .then((responseData) => responseData);

const isCredentialsDataExpired = () => {
  const currentTime = new Date();
  const diffTime = Math.abs(
    currentTime.getTime() - credentialsDataLastUpdated.getTime(),
  );
  const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
  return diffHours > credentialsExpireTimeInHours;
};

/**
 * The wrapping retry method. The main argument is the method-callback with a token and the success and failure methods.
 */
const fetchCredentialsData = async (retry?: boolean): Promise<Credentials> => {
  try {
    return await authenticate();
  } catch (error) {
    if (retry) {
      return fetchCredentialsData(true);
    } else {
      throw new CybwellError("Authentication failed " + error);
    }
  }
};

const isTokenInvalid = (cybwellResponse: CybwellResponse<unknown>) => {
  return (
    cybwellResponse.RESULT.TRX.ERRORMSG &&
    cybwellResponse.RESULT.TRX.ERRORMSG === "not authenticated"
  );
};

const getCybwellRequestWithCredentials = async <T>(
  url: string,
  urlParameters?: object,
  otherParams?: RequestInit,
  credentialsRequired?: boolean,
): Promise<T> => {
  if (!credentialsData || isCredentialsDataExpired()) {
    credentialsData = await fetchCredentialsData();
    credentialsDataLastUpdated = new Date();
  }

  return get(
    url,
    {
      ...urlParameters,
      style: "JSON",
      ...resolveCredentialsUrlParams(credentialsData),
    },
    otherParams,
    timeout,
  )
    .then((response) => processResponse(response))
    .then((processedResponse) => {
      if (
        processedResponse.RESULT.TRX.STATUS === "0" &&
        isTokenInvalid(processedResponse)
      ) {
        if (!credentialsRequired) {
          return getCybwellRequestWithCredentials(
            url,
            urlParameters,
            otherParams,
            true,
          );
        } else {
          throw new CybwellError(
            "Retry for getting authentication token failed",
          );
        }
      }

      return processedResponse;
    });
};

/**
 * Suggest the company with the query.
 */
export const suggestCompany = debounce(async (query: string) => {
  const responseData =
    await getCybwellRequestWithCredentials<CompaniesResponse>(
      `${CYBWELL_SERVICE_URL}/suggestCompany.ashx`,
      {
        q: query,
      },
    );

  if (responseData.RESULT.TRX.STATUS === "1") {
    if (responseData.RESULT.COMPANIES) {
      return mapSuggestCompanyResponse(responseData.RESULT.COMPANIES.COMPANY);
    } else {
      return [];
    }
  } else {
    throw new CybwellError(
      `Error response received from Cybwell while invoking 'suggestCompany' with query ${query} and TRX: ${JSON.stringify(
        responseData?.RESULT?.TRX,
      )}`,
      responseData.RESULT.TRX.STATUS,
    );
  }
}, debounceCallbackTimeout);

/**
 * Gets the company details by the ehraid that was received before.
 */
export const getCompanyDetail = async (ehraid: string) => {
  const responseData =
    await getCybwellRequestWithCredentials<CompanyDetailResponse>(
      `${CYBWELL_SERVICE_URL}/getCompanyDetail.ashx`,
      {
        EHRAID: ehraid,
        include: "noga",
      },
    );
  if (responseData.RESULT.TRX.STATUS === "1") {
    return mapCompanyDetailResponse(responseData.RESULT.COMPANYDETAIL);
  } else {
    throw new CybwellError(
      `Error response received from Cybwell while invoking 'getCompanyDetail' with ehraid ${ehraid} and TRX: ${JSON.stringify(
        responseData?.RESULT?.TRX,
      )}`,
      responseData.RESULT.TRX.STATUS,
    );
  }
};

/**
 * Gets the bank data by entering an iban.
 */
export const getBankByIBAN = async (query: string) => {
  const responseData = await getCybwellRequestWithCredentials<BankResponse>(
    `${CYBWELL_SERVICE_URL}/getBankByIBAN.ashx`,
    {
      q: query,
    },
  );

  if (responseData.RESULT.TRX.STATUS === "1") {
    const banks = responseData.RESULT.BANKS;

    if (banks === null) {
      return {
        code: null,
        name: null,
      };
    }

    return mapGetBankByIBANResponse(responseData.RESULT.BANKS.BANK);
  } else {
    throw new CybwellError(
      `Error response received from Cybwell while invoking 'getBankByIBAN' with query ${query} and TRX: ${JSON.stringify(
        responseData?.RESULT?.TRX,
      )}`,
      responseData.RESULT.TRX.STATUS,
    );
  }
};

/**
 * Gets the address based on an entered substring.
 */
export const suggestAddress = debounce(async (query: string) => {
  const responseData = await getCybwellRequestWithCredentials<AdressesResponse>(
    `${CYBWELL_SERVICE_URL}/suggestAdress.ashx`,
    {
      q: query,
    },
  );

  if (responseData.RESULT.TRX.STATUS === "1") {
    if (responseData.RESULT.ADRESSES) {
      return mapSuggestAddressResponse(responseData.RESULT.ADRESSES.ADRESS);
    } else {
      return [];
    }
  } else {
    throw new CybwellError(
      `Error response received from Cybwell while invoking 'suggestAddress' with query ${query} and TRX: ${JSON.stringify(
        responseData?.RESULT?.TRX,
      )}`,
      responseData.RESULT.TRX.STATUS,
    );
  }
}, debounceCallbackTimeout);

/**
 * Gets the address based on an entered substring.
 */
export const getAddressDetail = async (legacyID: string) => {
  const responseData =
    await getCybwellRequestWithCredentials<AdressDetailResponse>(
      `${CYBWELL_SERVICE_URL}/getAdressDetail.ashx`,
      {
        legacyID,
      },
    );

  if (responseData.RESULT.TRX.STATUS === "1") {
    return mapAddressDetailResponse(responseData.RESULT.ADRESSES.ADRESSDETAIL);
  } else {
    throw new CybwellError(
      `Error response received from Cybwell while invoking 'getAddressDetail' with legacyID ${legacyID} and TRX: ${JSON.stringify(
        responseData?.RESULT?.TRX,
      )}`,
      responseData.RESULT.TRX.STATUS,
    );
  }
};
